import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, FileText, Users, Shield, Activity, Clock, Loader2,
  CheckCircle2, FolderOpen, Sparkles, AlertTriangle, MapPin,
  Scale, Gavel, Download, Eye, BarChart3, Truck, Camera, Briefcase
} from "lucide-react";
import { API } from "@/constants";

const ICON_MAP = {
  briefcase: Briefcase, file: FileText, sparkles: Sparkles, truck: Truck,
  check: CheckCircle2, camera: Camera, activity: Activity, shield: Shield,
};
const COLOR_MAP = {
  blue: "bg-blue-100 text-blue-600 border-blue-200",
  emerald: "bg-emerald-100 text-emerald-600 border-emerald-200",
  purple: "bg-purple-100 text-purple-600 border-purple-200",
  teal: "bg-teal-100 text-teal-600 border-teal-200",
  green: "bg-green-100 text-green-600 border-green-200",
  amber: "bg-amber-100 text-amber-600 border-amber-200",
  red: "bg-red-100 text-red-600 border-red-200",
};

const CaseDashboardPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    const fetchCase = async () => {
      try {
        const [caseRes, timelineRes] = await Promise.all([
          axios.get(`${API}/dashboard/case/${caseId}`, { withCredentials: true }),
          axios.get(`${API}/dashboard/case/${caseId}/timeline`, { withCredentials: true }).catch(() => ({ data: { timeline: [] } })),
        ]);
        setData(caseRes.data);
        setTimeline(timelineRes.data.timeline || []);
      } catch (e) {
        console.error("Case dashboard error:", e);
      } finally {
        setLoading(false);
      }
    };
    if (caseId) fetchCase();
  }, [caseId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );

  if (!data?.case) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Card className="max-w-md">
        <CardContent className="pt-6 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h3 className="font-semibold mb-2">Case Not Found</h3>
          <p className="text-sm text-muted-foreground mb-4">This case may have been removed or doesn't exist.</p>
          <Link to="/dashboard"><Button>Back to Dashboard</Button></Link>
        </CardContent>
      </Card>
    </div>
  );

  const c = data.case;
  const parties = c.parties || [];
  const torts = c.torts || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="case-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-3">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <Scale className="h-6 w-6 text-blue-400" />
                <h1 className="text-xl font-bold">{c.case_name || "Untitled Case"}</h1>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-300 mt-2">
                {c.case_number && <span>#{c.case_number}</span>}
                {c.state && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{c.state}{c.county ? `, ${c.county}` : ""}</span>}
                {c.court_name && <span className="flex items-center gap-1"><Gavel className="h-3 w-3" />{c.court_name}</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <Link to={`/tools/litix-wizard`}>
                <Button size="sm" variant="outline" className="border-slate-600 text-slate-200 hover:bg-slate-800">
                  <Sparkles className="h-4 w-4 mr-1" /> Open in Wizard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Documents", value: data.summary.total_documents, icon: FileText, color: "text-blue-600", bg: "bg-blue-50" },
            { label: "AI Reports", value: data.summary.total_reports, icon: Sparkles, color: "text-purple-600", bg: "bg-purple-50" },
            { label: "Services", value: data.summary.total_services, icon: Truck, color: "text-emerald-600", bg: "bg-emerald-50" },
            { label: "Monitoring", value: data.summary.monitoring_active ? "Active" : "Inactive", icon: Activity, color: data.summary.monitoring_active ? "text-green-600" : "text-slate-400", bg: data.summary.monitoring_active ? "bg-green-50" : "bg-slate-50" },
          ].map((s, i) => (
            <Card key={i} data-testid={`case-stat-${s.label.toLowerCase()}`}>
              <CardContent className="pt-4 pb-4 px-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${s.bg}`}>
                  <s.icon className={`h-5 w-5 ${s.color}`} />
                </div>
                <div>
                  <p className="text-xl font-bold">{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Case Details */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-base">Case Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {c.case_summary && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Summary</p>
                  <p className="text-sm">{c.case_summary}</p>
                </div>
              )}
              {c.damages_claimed && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Damages Claimed</p>
                  <p className="text-lg font-bold text-emerald-700">${Number(c.damages_claimed).toLocaleString()}</p>
                </div>
              )}
              {c.judge_name && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Judge</p>
                  <p className="text-sm font-medium">{c.judge_name}</p>
                </div>
              )}
              {c.filing_date && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Filing Date</p>
                  <p className="text-sm">{c.filing_date}</p>
                </div>
              )}
              {torts.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Causes of Action</p>
                  <div className="flex flex-wrap gap-1">
                    {torts.map((t, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{t}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Parties */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" /> Parties ({parties.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {parties.length === 0 ? (
                <p className="text-sm text-slate-500">No parties added</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-3">
                  {parties.map((p, i) => (
                    <div key={i} className={`p-3 rounded-lg border ${p.role === 'plaintiff' ? 'border-blue-200 bg-blue-50/50' : 'border-red-200 bg-red-50/50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-xs ${p.role === 'plaintiff' ? 'bg-blue-600' : 'bg-red-600'}`}>{p.role}</Badge>
                        <span className="text-xs text-slate-500 capitalize">{p.party_type}</span>
                      </div>
                      <p className="font-medium text-sm">{p.name || "Unnamed"}</p>
                      {p.attorney_name && <p className="text-xs text-slate-500 mt-1">Counsel: {p.attorney_name}{p.attorney_firm ? `, ${p.attorney_firm}` : ""}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* AI Reports */}
        {data.reports?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-600" /> AI Analysis Reports ({data.reports.length})
              </CardTitle>
              <CardDescription>AI-generated intelligence and analysis for this case</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data.reports.map((r, i) => (
                  <div key={i} className="p-4 bg-slate-50 rounded-lg border">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-sm">Litix Analysis Report</p>
                        <p className="text-xs text-slate-500">{r.generated_at || r.created_at || "Generated"}</p>
                      </div>
                      <Badge className="bg-purple-100 text-purple-800 text-xs">AI Generated</Badge>
                    </div>
                    {r.summary && <p className="text-sm text-slate-700 mb-3">{typeof r.summary === 'string' ? r.summary.slice(0, 300) : JSON.stringify(r.summary).slice(0, 300)}...</p>}
                    {r.key_findings && (
                      <div className="space-y-1">
                        {(Array.isArray(r.key_findings) ? r.key_findings : []).slice(0, 3).map((f, j) => (
                          <div key={j} className="flex items-start gap-2">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                            <p className="text-xs text-slate-600">{typeof f === 'string' ? f : f.finding || JSON.stringify(f)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Documents */}
        {data.documents?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-blue-600" /> Case Documents ({data.documents.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.documents.map((d, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                    <FileText className="h-5 w-5 text-blue-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{d.name || d.doc_name || "Document"}</p>
                      <p className="text-xs text-slate-500">{d.type || d.doc_type || "File"}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Service Requests */}
        {data.service_requests?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="h-4 w-4 text-emerald-600" /> Service Requests ({data.service_requests.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.service_requests.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <Shield className="h-4 w-4 text-emerald-500" />
                      <div>
                        <p className="text-sm font-medium capitalize">{(s.service_type || "").replace(/_/g, " ")}</p>
                        <p className="text-xs text-slate-500">{s.requested_at || s.created_at || ""}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs capitalize">{s.status || "pending"}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Case Timeline */}
        {timeline.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4 text-blue-600" /> Case Timeline ({timeline.length} events)
              </CardTitle>
              <CardDescription>Chronological history of all case activity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative" data-testid="case-timeline">
                {/* Vertical line */}
                <div className="absolute left-[19px] top-0 bottom-0 w-px bg-slate-200" />
                
                <div className="space-y-4">
                  {timeline.map((event, i) => {
                    const IconComp = ICON_MAP[event.icon] || Activity;
                    const colorClass = COLOR_MAP[event.color] || COLOR_MAP.blue;
                    
                    return (
                      <div key={i} className="relative flex gap-4" data-testid={`timeline-event-${i}`}>
                        {/* Icon */}
                        <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${colorClass}`}>
                          <IconComp className="h-4 w-4" />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 pb-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm font-medium">{event.title}</p>
                              {event.description && (
                                <p className="text-xs text-slate-500 mt-0.5">{event.description.slice(0, 150)}</p>
                              )}
                              {event.verified_by && (
                                <p className="text-xs text-slate-400 mt-0.5">By: {event.verified_by}</p>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0 ml-4">
                              <Badge variant="outline" className="text-[10px] capitalize">{event.type.replace(/_/g, " ")}</Badge>
                              {event.timestamp && (
                                <p className="text-[10px] text-slate-400 mt-1">
                                  {event.timestamp.split("T")[0]}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Case Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { to: "/tools/investigator", label: "Investigate Party", icon: Eye, color: "text-red-600" },
                { to: "/scanner", label: "Scan Document", icon: Camera, color: "text-indigo-600" },
                { to: "/tools/opening", label: "Draft Opening", icon: Scale, color: "text-blue-600" },
                { to: "/tools/demand-letter", label: "Demand Letter", icon: FileText, color: "text-emerald-600" },
                { to: "/tools/carrier-scorecard", label: "Carrier Intel", icon: Shield, color: "text-purple-600" },
              ].map((a, i) => (
                <Link key={i} to={a.to}>
                  <Button variant="outline" className="w-full h-auto py-3 flex flex-col items-center gap-1.5" data-testid={`case-action-${a.label.toLowerCase().replace(/ /g,'-')}`}>
                    <a.icon className={`h-5 w-5 ${a.color}`} />
                    <span className="text-xs">{a.label}</span>
                  </Button>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CaseDashboardPage;
